// Comparison Store - prosty globalny state dla porównania produktów
export interface ComparisonProduct {
    name: string;
    price: number;
    imageUrl: string;
    slug: string;
    brand?: string;
    diameter?: string;
    width?: string;
    et?: string;
    pcd?: string;
    color?: string;
    weight?: string;
    material?: string;
    stock?: number;
}

class ComparisonStore {
    private products: ComparisonProduct[] = [];
    private listeners: Set<() => void> = new Set();
    private readonly MAX_PRODUCTS = 4;

    constructor() {
        // Load from localStorage (tylko w przeglądarce)
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
            const saved = localStorage.getItem('comparison-products');
            if (saved) {
                try {
                    this.products = JSON.parse(saved);
                    console.log('✅ Comparison store loaded:', this.products.length, 'products');
                } catch (e) {
                    console.error('❌ Failed to load comparison products:', e);
                }
            }
        }
    }

    subscribe(listener: () => void) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notify() {
        this.listeners.forEach(listener => {
            try {
                listener();
            } catch (e) {
                console.error('❌ Listener error:', e);
            }
        });
        
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
            try {
                localStorage.setItem('comparison-products', JSON.stringify(this.products));
                console.log('✅ Comparison saved:', this.products.length, 'products');
            } catch (e) {
                console.error('❌ Failed to save comparison:', e);
            }
        }
    }

    getProducts(): ComparisonProduct[] {
        return [...this.products];
    }

    getCount(): number {
        return this.products.length;
    }

    hasProduct(slug: string): boolean {
        return this.products.some(p => p.slug === slug);
    }

    canAdd(): boolean {
        return this.products.length < this.MAX_PRODUCTS;
    }

    addProduct(product: ComparisonProduct): boolean {
        console.log('📥 Adding product:', product.name, product.slug);
        
        if (this.hasProduct(product.slug)) {
            console.log('⚠️ Product already in comparison');
            return false; // Already exists
        }
        if (!this.canAdd()) {
            alert(`Możesz porównać maksymalnie ${this.MAX_PRODUCTS} produkty jednocześnie.`);
            console.log('⚠️ Max products reached');
            return false;
        }
        this.products.push(product);
        console.log('✅ Product added. Total:', this.products.length);
        this.notify();
        return true;
    }

    removeProduct(slug: string): void {
        this.products = this.products.filter(p => p.slug !== slug);
        this.notify();
    }

    clear(): void {
        console.log('🗑️ Clearing comparison');
        this.products = [];
        this.notify();
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
            localStorage.removeItem('comparison-products');
        }
    }

    toggleProduct(product: ComparisonProduct): boolean {
        if (this.hasProduct(product.slug)) {
            this.removeProduct(product.slug);
            return false; // Removed
        } else {
            return this.addProduct(product); // Added (or failed)
        }
    }
}

export const comparisonStore = new ComparisonStore();
